import { prefersReducedMotion } from "../utils/reduced-motion";

const STORAGE_KEY = "modulo-color-focus";
const VALID_FOCUS = new Set(["main", "header"]);
const VALID_MODES = new Set(["click", "hover_delay", "hover_header_instant"]);
const HOVER_DELAY_MS = 400;
/** Cursor ring that fills during hover_delay color switches. Set true to re-enable. */
const COLOR_SWITCH_INDICATOR_ENABLED = false;

let cleanup = null;
let hoverTimeout = null;
let indicatorEl = null;

function getStoredColorFocus() {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return VALID_FOCUS.has(value) ? value : null;
  } catch (error) {
    return null;
  }
}

function storeColorFocus(focus) {
  try {
    sessionStorage.setItem(STORAGE_KEY, focus);
  } catch (error) {
    // Ignore storage errors.
  }
}

function setColorFocus(focus) {
  if (document.body.dataset.colorFocus !== focus) {
    document.body.dataset.colorFocus = focus;
  }

  if (getSwitchMode() !== "hover_header_instant") {
    storeColorFocus(focus);
  }
}

function syncBodyZoneClasses() {
  const body = document.body;

  if (body.classList.contains("page-home")) {
    body.classList.remove("has-color-zones", "has-color-zones--fixed");
    delete body.dataset.colorFocus;
    return;
  }

  body.classList.add("has-color-zones");

  // Full project pages: keep colored header + B&W main, no zone switch
  // (avoids plan-mask / media fights). Modal overlay is separate.
  if (body.classList.contains("page-project")) {
    body.classList.add("has-color-zones--fixed");
    body.dataset.colorFocus = "main";
    return;
  }

  body.classList.remove("has-color-zones--fixed");
}

function toggleColorFocus() {
  const current = document.body.dataset.colorFocus || "main";
  setColorFocus(current === "main" ? "header" : "main");
}

function isInteractiveTarget(target) {
  return Boolean(
    target.closest("a, button, input, select, textarea, label")
  );
}

/** Nav controls that must keep their own click behavior on touch. */
function isColorZoneControlTarget(target) {
  return Boolean(
    target.closest(
      "button, input, select, textarea, label, .js-mobile-nav-toggle, .js-contact-toggle, .js-lang-toggle, .c-primary-nav__link--lang, .c-primary-nav__panel a, .c-primary-nav__panel button, .c-header-contact a, .c-header-contact button"
    )
  );
}

function isMaLogoTarget(target) {
  return Boolean(target.closest(".c-ma-logo"));
}

function getSwitchMode() {
  const mode = document.body.dataset.colorSwitchMode;
  return VALID_MODES.has(mode) ? mode : "hover_delay";
}

function canUseHover() {
  return (
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

function ensureIndicator() {
  if (!COLOR_SWITCH_INDICATOR_ENABLED) {
    return null;
  }

  if (indicatorEl) {
    return indicatorEl;
  }

  indicatorEl = document.createElement("div");
  indicatorEl.className = "c-color-switch-indicator";
  indicatorEl.setAttribute("aria-hidden", "true");
  indicatorEl.innerHTML = `
    <svg class="c-color-switch-indicator__svg" viewBox="0 0 36 36" aria-hidden="true">
      <circle class="c-color-switch-indicator__track" cx="18" cy="18" r="15" pathLength="100" />
      <circle class="c-color-switch-indicator__progress" cx="18" cy="18" r="15" pathLength="100" />
    </svg>
  `.trim();
  document.body.appendChild(indicatorEl);

  return indicatorEl;
}

function updateIndicatorPosition(x, y) {
  if (!COLOR_SWITCH_INDICATOR_ENABLED) {
    return;
  }

  const el = ensureIndicator();
  if (!el) {
    return;
  }

  el.style.transform = `translate(${x}px, ${y}px)`;
}

function startIndicator() {
  if (!COLOR_SWITCH_INDICATOR_ENABLED || prefersReducedMotion()) {
    return;
  }

  const el = ensureIndicator();
  if (!el) {
    return;
  }

  el.style.setProperty("--color-switch-delay", `${HOVER_DELAY_MS}ms`);
  el.classList.add("is-active");
  el.classList.remove("is-animating");
  void el.offsetWidth;
  el.classList.add("is-animating");
}

function stopIndicator() {
  if (!indicatorEl) {
    return;
  }

  indicatorEl.classList.remove("is-active", "is-animating");
}

function clearHoverTimeout() {
  if (hoverTimeout !== null) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }

  stopIndicator();
}

function scheduleColorFocus(focus) {
  clearHoverTimeout();

  const current = document.body.dataset.colorFocus || "main";
  if (current === focus) {
    return;
  }

  startIndicator();

  hoverTimeout = window.setTimeout(() => {
    hoverTimeout = null;
    setColorFocus(focus);
    stopIndicator();
  }, HOVER_DELAY_MS);
}

function bindHeaderInstantHover(header) {
  const onHeaderEnter = () => {
    setColorFocus("header");
  };

  const onHeaderLeave = () => {
    setColorFocus("main");
  };

  header.addEventListener("mouseenter", onHeaderEnter);
  header.addEventListener("mouseleave", onHeaderLeave);

  return () => {
    header.removeEventListener("mouseenter", onHeaderEnter);
    header.removeEventListener("mouseleave", onHeaderLeave);
  };
}

function bindHeaderInstantClick(header) {
  const onHeaderClick = (event) => {
    if (isColorZoneControlTarget(event.target)) {
      return;
    }

    // Touch: logo is the main colored surface — toggle instead of navigate home.
    if (isMaLogoTarget(event.target) && !canUseHover()) {
      event.preventDefault();
      const current = document.body.dataset.colorFocus || "main";
      setColorFocus(current === "main" ? "header" : "main");
      return;
    }

    if (isInteractiveTarget(event.target)) {
      return;
    }

    const current = document.body.dataset.colorFocus || "main";
    setColorFocus(current === "main" ? "header" : "main");
  };

  header.addEventListener("click", onHeaderClick);

  return () => {
    header.removeEventListener("click", onHeaderClick);
  };
}

function bindClickMode(header, main) {
  const onHeaderClick = (event) => {
    if (isColorZoneControlTarget(event.target)) {
      return;
    }

    // Touch: logo is the main colored surface — toggle instead of navigate home.
    if (isMaLogoTarget(event.target) && !canUseHover()) {
      event.preventDefault();
      toggleColorFocus();
      return;
    }

    if (isInteractiveTarget(event.target)) {
      return;
    }

    toggleColorFocus();
  };

  const onMainClick = (event) => {
    if (isInteractiveTarget(event.target)) {
      return;
    }

    toggleColorFocus();
  };

  header.addEventListener("click", onHeaderClick);
  main.addEventListener("click", onMainClick);

  return () => {
    header.removeEventListener("click", onHeaderClick);
    main.removeEventListener("click", onMainClick);
  };
}

function bindHoverMode(header, main) {
  const onPointerMove = (event) => {
    updateIndicatorPosition(event.clientX, event.clientY);
  };

  const onHeaderEnter = (event) => {
    updateIndicatorPosition(event.clientX, event.clientY);
    scheduleColorFocus("header");
  };

  const onMainEnter = (event) => {
    updateIndicatorPosition(event.clientX, event.clientY);
    scheduleColorFocus("main");
  };

  const onZoneLeave = () => {
    clearHoverTimeout();
  };

  header.addEventListener("mouseenter", onHeaderEnter);
  header.addEventListener("mousemove", onPointerMove);
  header.addEventListener("mouseleave", onZoneLeave);
  main.addEventListener("mouseenter", onMainEnter);
  main.addEventListener("mousemove", onPointerMove);
  main.addEventListener("mouseleave", onZoneLeave);

  return () => {
    clearHoverTimeout();
    header.removeEventListener("mouseenter", onHeaderEnter);
    header.removeEventListener("mousemove", onPointerMove);
    header.removeEventListener("mouseleave", onZoneLeave);
    main.removeEventListener("mouseenter", onMainEnter);
    main.removeEventListener("mousemove", onPointerMove);
    main.removeEventListener("mouseleave", onZoneLeave);
  };
}

export function initColorZones() {
  if (cleanup) {
    cleanup();
    cleanup = null;
  }

  stopIndicator();

  syncBodyZoneClasses();

  const header = document.getElementById("header");
  const main = document.getElementById("main");
  const body = document.body;

  if (!header || !main || !body.classList.contains("has-color-zones")) {
    return;
  }

  // Fixed pages (e.g. project): zones stay locked — no click/hover switch.
  if (body.classList.contains("has-color-zones--fixed")) {
    setColorFocus("main");
    return;
  }

  const mode = getSwitchMode();

  if (mode === "hover_header_instant") {
    cleanup = canUseHover()
      ? bindHeaderInstantHover(header)
      : bindHeaderInstantClick(header);
    setColorFocus("main");
    return;
  }

  const useHover = mode === "hover_delay" && canUseHover();
  cleanup = useHover ? bindHoverMode(header, main) : bindClickMode(header, main);

  setColorFocus(getStoredColorFocus() || "main");
}
